import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Form, Input, Modal, Select, notification } from "antd";
import { api } from "../utils/Api";
import { filterOption, getAvailableOptions } from "./shiftGroupUtils";

// Creates a default group of drivers of the plant, or edits group when it is passed.
// usedDrivers (already in other groups of the plant) are disabled.
const DriverDefaultGroupFormModal = ({
	open,
	onClose,
	onSuccess,
	plant,
	group,
	usedDrivers = [],
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [loadingDrivers, setLoadingDrivers] = useState(false);
	const [driverOptions, setDriverOptions] = useState([]);
	const [apiNotification, contextHolder] = notification.useNotification();

	const isEdit = !!group;

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		if (open && plant) {
			getDrivers();
		}
	}, [open, plant]);

	const getDrivers = async () => {
		const url = api + `/shift-group/drivers/${plant}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingDrivers(true);
			const { data } = await axios.get(url);
			if (data.success) {
				const options = data.payload.map((item) => ({
					label: item.name,
					value: item._id,
				}));
				// Members of the group that are no longer active still show their name
				const missingMembers = (group?.members || [])
					.filter((member) => !options.some((option) => option.value === member._id))
					.map((member) => ({ label: member.name, value: member._id }));
				setDriverOptions([...options, ...missingMembers]);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get drivers");
		} finally {
			setLoadingDrivers(false);
		}
	};

	const onFinish = async (values) => {
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoading(true);
			const { data } = isEdit
				? await axios.patch(api + `/shift-group-member/update/${group._id}`, {
						name: values.name,
						members: values.members,
				  })
				: await axios.post(api + "/shift-group-member/create", {
						name: values.name,
						plant: plant,
						members: values.members,
				  });
			if (data.success) {
				form.resetFields();
				onSuccess(data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification(
				"error",
				"Error",
				`Error while trying to ${isEdit ? "update" : "create"} group`,
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title={isEdit ? "Edit Group" : "Create New Group"}
			open={open}
			onCancel={onClose}
			centered
			destroyOnClose
			maskClosable={false}
			footer={[
				<Button key="cancel" size="large" onClick={onClose} disabled={loading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={loading}
					onClick={() => form.submit()}
				>
					Save
				</Button>,
			]}
		>
			{contextHolder}
			<Form
				form={form}
				layout="vertical"
				style={{ marginTop: "20px" }}
				onFinish={onFinish}
				preserve={false}
			>
				<Form.Item
					name="name"
					label="Name"
					initialValue={group?.name}
					rules={[
						{ required: true, whitespace: true, message: "Name is required" },
					]}
				>
					<Input size="large" placeholder="Group name" />
				</Form.Item>
				<Form.Item
					name="members"
					label="Drivers"
					initialValue={group?.members.map((member) => member._id)}
					rules={[{ required: true, message: "Select at least one driver" }]}
				>
					<Select
						mode="multiple"
						showSearch
						allowClear
						placeholder="Select drivers"
						size="large"
						options={getAvailableOptions(driverOptions, usedDrivers)}
						filterOption={filterOption}
						loading={loadingDrivers}
						disabled={loadingDrivers}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default DriverDefaultGroupFormModal;
